import {
  AnimationMixer,
  AxesHelper,
  Clock,
  Object3D,
  Quaternion,
  Vector2,
  Vector3,
} from "three";
import { ImageTargetPipelineModuleResult } from "./8thwall/image-target-pipeline-module";
import { ArtworkData, artworkData } from "./artworks";
import { I8thWallImageTargetEventModel } from "./8thwall";
import { EJPlayerBase } from "./libs/ejx.es";
import { RendererEvents } from ".";
import { Emitter } from "mitt";
import { TaggedFsm } from "@/utils/tagged-fsm";
import { ArtworkState } from "@/context/ArtworkContext";
import { setAnimations, startAnimation } from "./rubixContent";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import { ContentState, contentStateManager } from "./ContentStateManager";

type ITargetType = I8thWallImageTargetEventModel["detail"] & {
  distanceToScreenCenter?: number;
};
//
// FSM States
// eslint-disable-next-line @typescript-eslint/no-namespace
namespace S {
  export type None = { s: "None" };
  export type Scanning = { s: "Scanning" };
  export type Loading = { s: "Loading" };
  export type Viewing = { s: "Viewing" };
  export type All = None | Scanning | Loading | Viewing;
}

// FSM Events
// eslint-disable-next-line @typescript-eslint/no-namespace
namespace E {
  export type StartScanning = { e: "StartScanning"; args: [ArtworkData] };
  export type CubeDetected = { e: "CubeDetected"; args: [ArtworkData] };
  export type StartViewingContent = { e: "StartViewingContent"; args: [] };
  export type UpdateContentView = {
    e: "UpdateContentView";
    args: [ArtworkData];
  };
  export type EnterContent = { e: "EnterContent"; args: [number] };
  export type ExitContent = { e: "ExitContent"; args: [] };
  export type UpdateTarget = {
    e: "UpdateTarget";
    args: [
      transformation: {
        position: Vector3;
        rotation: Quaternion;
        scale: number;
      },
    ];
  };
  export type LoadingTarget = {
    e: "LoadingTarget";
    args: [
      transformation: {
        position: Vector3;
        rotation: Quaternion;
        scale: number;
      },
    ];
  };
  export type LoadComplete = { e: "LoadComplete"; args: [] };
  export type ContentLost = { e: "ContentLost"; args: [] };
  export type ContentReplace = { e: "ContentReplace"; args: [] };
  export type All =
    | StartScanning
    | CubeDetected
    | StartViewingContent
    | UpdateContentView
    | EnterContent
    | ExitContent
    | UpdateTarget
    | LoadingTarget
    | LoadComplete
    | ContentLost
    | ContentReplace;
}

export enum UIContentState {
  NONE,
  MAIN_MENU,
  STORY_CONTENT,
}

export function initImageTargetExperience(
  module: ImageTargetPipelineModuleResult | null,
  player: EJPlayerBase | undefined,
  emitter: Emitter<RendererEvents>,
  contentContainer: Object3D,
  models: GLTF[],
  // cubes: any,
  setEJXCube: (artwork: ArtworkData) => Promise<void>
) {
  const artworks = artworkData;
  // const { contentContainer, setContent, cubeProxy } = EJXCube;
  const targetsFound: ITargetType[] = [];
  const targetPosition3D = new Vector3();
  const targetPosition2D = new Vector2();
  const screenCenter = new Vector2(0.5, 0.5);
  const artworkActive = contentStateManager.getActiveArtwork();
  const getCurrentArtwork = () => contentStateManager.getActiveArtwork();
  let artworkActiveLostTime = 0.0;
  const artworkActiveLostTimeMax = 1.0;

  let pauseRender = false;

  const clock = new Clock(true);
  let deltaSeconds = 0;

  contentStateManager.init(contentContainer);
  let canDetectCube = true;
  let transitionTriggered = false;

  let lastContentState = contentStateManager.getState();

  const mixers: AnimationMixer[] = [];
  const animContentIn = setAnimations(
    contentContainer,
    models[0],
    "animContentIn"
  );
  const animContentOut = setAnimations(
    contentContainer,
    models[1],
    "animContentOut"
  );
  mixers.push(animContentIn.mixer, animContentOut.mixer);

  animContentIn.mixer.addEventListener("finished", () => {
    contentStateManager.setState(ContentState.INTRO_ANIM);
    emitter.emit("set-ui-state", UIContentState.MAIN_MENU);
  });

  animContentOut.mixer.addEventListener("finished", () => {
    if (
      contentStateManager.getState() === ContentState.CONTENT_IN_TRANSITION &&
      fsm.can("StartViewingContent")
    ) {
      fsm.dispatch("StartViewingContent");
    }
    emitter.emit("set-ui-state", UIContentState.STORY_CONTENT);
  });

  module?.emitter.emit("artwork-state", ArtworkState.PLACING);

  let contentId = 0;

  const fsm = new TaggedFsm<S.All, E.All>({ s: "None" });

  fsm.defineTransition("None", "StartScanning", (_prev, artwork) => {
    contentStateManager.setState(ContentState.HIDDEN);
    module?.emitter.emit("artwork-state", ArtworkState.PLACING);
    return { s: "Scanning", artwork };
  });
  fsm.defineTransition("Scanning", "CubeDetected", (prev, currentArtwork) => {
    canDetectCube = false;
    module?.emitter.emit("artwork-state", ArtworkState.VIEWING);

    contentStateManager.setState(ContentState.INTRO_ANIM);
    contentStateManager.resetFirstDetectedFace();
    contentStateManager.setFirstDetectedFace(currentArtwork.targetName);
    contentStateManager.orientContent();
    startAnimation(animContentIn.actions);
    return { ...prev, s: "Viewing" };
  });

  fsm.defineTransition("Viewing", "EnterContent", (prev, id) => {
    const lerp =
      contentStateManager.getFirstDetectedFace() !==
      contentStateManager.getActiveArtwork()?.targetName;
    contentStateManager.resetFirstDetectedFace();
    contentStateManager.orientContent(lerp);
    contentStateManager.setState(ContentState.OUTRO_ANIM);
    startAnimation(animContentOut.actions);
    emitter.emit("set-ui-state", UIContentState.NONE);
    return { ...prev, s: "Viewing" };
  });
  fsm.defineTransition("Viewing", "ExitContent", (prev) => {
    emitter.emit("set-ui-state", UIContentState.NONE);
    startAnimation(animContentIn.actions);
    emitter.emit("start-transition", {
      contentState: ContentState.CONTENT_OUT_TRANSITION,
      animationState: "out",
    });
    return { ...prev, s: "Viewing" };
  });
  fsm.defineTransition("Viewing", "StartViewingContent", (prev) => {
    contentStateManager.setState(ContentState.PORTAL);
    return { ...prev, s: "Viewing" };
  });
  fsm.defineTransition("Viewing", "UpdateContentView", (prev) => {
    contentStateManager.orientContent();
    return { ...prev, s: "Viewing" };
  });
  fsm.defineTransition(
    "Scanning",
    "UpdateContentView",
    (prev, currentArtwork) => {
      module?.emitter.emit("artwork-state", ArtworkState.VIEWING);
      contentStateManager.setFirstDetectedFace(currentArtwork.targetName);
      contentStateManager.orientContent();

      return { ...prev, s: "Viewing" };
    }
  );

  fsm.defineTransition("Viewing", "UpdateTarget", (prev, transformation) => {
    contentStateManager.applyTrackingMatrix(transformation);
    return { ...prev, s: "Viewing" };
  });

  fsm.defineTransition("Viewing", "ContentLost", (prev) => {
    contentContainer.visible = false;
    contentStateManager.resetFirstDetectedFace();
    module?.emitter.emit("artwork-state", ArtworkState.PLACING);
    return { ...prev, s: "Scanning" };
  });

  fsm.defineTransition("Viewing", "ContentReplace", (prev) => {
    return { ...prev, s: "Scanning" };
  });

  if (fsm.can("StartScanning")) {
    fsm.dispatch("StartScanning", artworkData[1]);
  } else {
    console.log("FSM cannot StartScanning from state", fsm.value);
  }

  const hitMarker = new AxesHelper();
  hitMarker.visible = import.meta.env.DEV;
  player.scene.add(hitMarker);

  let oldProgress = 0;
  let contentIsLoading = false;

  emitter.on(
    "start-transition",
    ({
      contentState,
      animationState,
    }: {
      contentState: ContentState;
      animationState: "in" | "out";
    }) => {
      contentStateManager.setState(contentState);
    }
  );

  player.renderOverrideFunc = () => {
    const deltaTime = clock.getDelta();
    contentStateManager.updateOrientationLerp();

    const currentState = contentStateManager.getState();
    if (currentState !== lastContentState) {
      lastContentState = currentState;
      transitionTriggered = false;
    }

    if (contentIsLoading) {
      const newProgress = player.contentLoadProgress();
      if (player.contentLoaded()) {
        if (emitter.emit) {
          emitter.emit("content-load-progress", 1);
          emitter.emit("content-loaded");
        }
        if (fsm.can("EnterContent")) {
          fsm.dispatch("EnterContent", contentId);
        }
        contentIsLoading = false;
      } else if (oldProgress !== newProgress) {
        oldProgress = newProgress;
        if (emitter.emit) emitter.emit("content-load-progress", newProgress);
      }
    }

    if (mixers) mixers.forEach((mixer) => mixer.update(deltaTime));

    if (!transitionTriggered) {
      if (currentState === ContentState.OUTRO_ANIM) {
        if (
          animContentOut.actions.values().next().value!.time >= 1.75 &&
          !transitionTriggered
        ) {
          if (emitter.emit)
            emitter.emit("start-transition", {
              contentState: ContentState.CONTENT_IN_TRANSITION,
              animationState: "in",
            });

          transitionTriggered = true;
        }
      }
    }
  };

  module?.emitter?.on("content-select", async (id: number) => {
    contentId = id;
    contentIsLoading = true;
    emitter.emit("set-ui-state", UIContentState.NONE);
    switch (id) {
      case 0:
        await setEJXCube(artworkData[0]);
        break;
      case 1:
        await setEJXCube(artworkData[1]);
        break;
      case 2:
        await setEJXCube(artworkData[2]);
        break;
      default:
        break;
    }
  });

  module?.emitter?.on("animate-content-out", () => {
    if (fsm.can("ExitContent")) {
      fsm.dispatch("ExitContent");
    }
  });

  module?.emitter?.on("resume-tracking", () => (pauseRender = false));
  module?.emitter?.on("pause-tracking", () => (pauseRender = true));
  module?.emitter?.on("fake-content-found", () => {
    if (fsm.can("CubeDetected")) {
      fsm.dispatch("CubeDetected", artworkData[1]);
    }
  });

  const lastTrackedPosition = new Vector3();
  const lastTrackedRotation = new Quaternion();
  const lastTrackedScale = new Vector3();

  module?.emitter?.on("on-update-scene", () => {
    deltaSeconds = clock.getDelta();

    if (pauseRender) {
      if (contentStateManager.getActiveArtwork()) {
        fsm.dispatch("ContentLost");
        contentStateManager.setActiveArtwork(null);
      }
      return;
    }

    for (let i = 0; i < targetsFound.length; i++) {
      const target = targetsFound[i];
      targetPosition3D.copy(target.position);
      targetPosition3D.project(player.camera);
      targetPosition2D.x = (targetPosition3D.x + 1) / 2;
      targetPosition2D.y = (-targetPosition3D.y + 1) / 2;
      target.distanceToScreenCenter = targetPosition2D.distanceTo(screenCenter);
    }

    targetsFound.sort((a, b) => {
      return a.distanceToScreenCenter && b.distanceToScreenCenter
        ? a.distanceToScreenCenter - b.distanceToScreenCenter
        : 0;
    });

    let targetFound: ITargetType | null = null;
    let artworkFound: ArtworkData | null = null;
    const activeArtwork = contentStateManager.getActiveArtwork(); // Always get latest active artwork

    if (activeArtwork) {
      for (let i = 0; i < targetsFound.length; i++) {
        if (activeArtwork.targetName === targetsFound[i].name) {
          artworkFound = activeArtwork;
          targetFound = targetsFound[i];
          break;
        }
      }
    }

    if (!artworkFound && targetsFound.length > 0) {
      const targetFirst = targetsFound[0];
      for (let i = 0; i < artworks.length; i++) {
        if (artworks[i].targetName === targetFirst.name) {
          artworkFound = artworks[i];
          targetFound = targetFirst;
          break;
        }
      }
    }

    if (activeArtwork) {
      if (artworkFound) {
        if (activeArtwork.targetName === artworkFound.targetName) {
          artworkActiveLostTime = 0.0;
        }
      } else {
        artworkActiveLostTime += deltaSeconds;
      }
    }

    const bReplace =
      artworkFound !== null &&
      activeArtwork !== null &&
      activeArtwork.targetName !== artworkFound.targetName;
    const bTimeout = artworkActiveLostTime >= artworkActiveLostTimeMax;

    if (bTimeout) {
      artworkActiveLostTime = artworkActiveLostTimeMax;
    }

    const bStop = activeArtwork !== null && (bReplace || bTimeout);
    if (bStop) {
      if (bReplace) {
        if (fsm.can("ContentReplace")) {
          fsm.dispatch("ContentReplace");
          contentStateManager.setActiveArtwork(null);
        }
      } else {
        fsm.dispatch("ContentLost");
        contentStateManager.setActiveArtwork(null);
        artworkActiveLostTime = 0.0;
      }
    }

    let bPlay = artworkFound !== null && activeArtwork === null;
    if (bPlay) {
      contentStateManager.setActiveArtwork(artworkFound);
      artworkActiveLostTime = 0.0;

      const newActiveArtwork = contentStateManager.getActiveArtwork();
      if (!newActiveArtwork) return;

      module.emitter.emit("on-artwork-change", newActiveArtwork);
      if (canDetectCube) {
        fsm.dispatch("CubeDetected", newActiveArtwork);
      } else {
        if (fsm.can("UpdateContentView")) {
          fsm.dispatch("UpdateContentView", newActiveArtwork);
        }
      }
    }

    if (targetFound && fsm.can("LoadingTarget")) {
      fsm.dispatch("LoadingTarget", {
        position: targetFound.position as Vector3,
        rotation: targetFound.rotation as Quaternion,
        scale: targetFound.scale,
      });
    }

    const bShowVideo =
      contentStateManager.getActiveArtwork() !== null && targetFound !== null;
    if (bShowVideo) {
      if (fsm.can("UpdateTarget")) {
        fsm.dispatch("UpdateTarget", {
          position: targetFound!.position as Vector3,
          rotation: targetFound!.rotation as Quaternion,
          scale: targetFound!.scale,
        });
        lastTrackedPosition.copy(contentContainer.position);
        lastTrackedRotation.copy(contentContainer.quaternion);
        lastTrackedScale.copy(contentContainer.scale);
      }
    } else {
      contentContainer.position.copy(lastTrackedPosition);
      contentContainer.quaternion.copy(lastTrackedRotation);
      contentContainer.scale.copy(lastTrackedScale);
    }
  });

  module?.emitter?.on("on-show-target", async ({ detail }) => {
    targetsFound.push(detail);
  });

  module?.emitter?.on("on-update-target", ({ detail }) => {
    for (let i = 0; i < targetsFound.length; i++) {
      if (targetsFound[i].name == detail.name) {
        targetsFound[i] = detail;
        break;
      }
    }
  });

  module?.emitter?.on("on-hide-target", ({ detail }) => {
    for (let i = 0; i < targetsFound.length; i++) {
      if (targetsFound[i].name == detail.name) {
        targetsFound.splice(i, 1);
        break;
      }
    }
  });
}
